/* eslint-disable no-alert */
/* eslint-disable consistent-return */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { trim } from 'ramda';
import debounce from 'lodash/debounce';
import { useNotify } from 'react-admin';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  Paper,
  List,
  Box,
  ListItem
} from '@material-ui/core';
import { useField } from 'react-final-form';
import { makeStyles } from '@material-ui/core/styles';
import AddressDialog from './AddressDialog';

const useStyles = makeStyles((theme) => ({
  searchBox: {
    marginBottom: theme.spacing(2)
  },
  suggestions: {
    position: 'absolute',
    // top: '80px',
    width: '100%',
    maxHeight: '200px',
    overflowY: 'auto',
    zIndex: 100,
    boxShadow: '0px 4px 8px rgba(0,0,0,0.2)'
  },
  mapContainer: {
    width: '100%',
    height: '400px',
    display: 'block',
    marginTop: theme.spacing(2)
  },
  selectedAddress: {
    marginTop: theme.spacing(1),
    fontWeight: 'bold',
    color: 'green'
  },
  addressLink: {
    cursor: 'pointer',
    marginRight: 10,
    color: 'blue',
    textDecoration: 'underline'
  }
}));

const AddressField = ({
  required = false,
  onAddressSelect = () => {},
  ...props
}) => {
  const notify = useNotify();
  const classes = useStyles();
  const [address, setAddress] = useState(props.record?.address || '');
  const [searchInput, setSearchInput] = useState(''); // New state for search input
  const [open, setOpen] = useState(false);
  const [location, setLocation] = useState({
    lat: props.record?.latitude || 32.084041,
    lng: props.record?.longitude || 34.887762
  });
  const [googleMapsReady, setGoogleMapsReady] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [initialAddressBeforeDialog, setInitialAddressBeforeDialog] =
    useState('');

  const { REACT_APP_GOOGLE_MAPS_API_KEY } = process.env;

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapContainerRef = useRef(null);
  const scriptLoadedRef = useRef(false);

  const {
    input: { onChange: changeLongitude }
  } = useField('longitude');
  const {
    input: { onChange: changeLatitude }
  } = useField('latitude');
  const {
    input: { onChange: changeCity }
  } = useField('city');
  const {
    input: { onChange: changeStreet }
  } = useField('street');
  const {
    input: { onChange: changeAddress }
  } = useField('address');
  const {
    input: { onChange: changeDistrict }
  } = useField('district');

  const loadGoogleMaps = (callback) => {
    if (window.google && window.google.maps) {
      scriptLoadedRef.current = true;
      callback();
      return;
    }

    if (!scriptLoadedRef.current) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        scriptLoadedRef.current = true;
        callback();
      };
      document.body.appendChild(script);
    }
  };

  const initializeMap = () => {
    if (!mapContainerRef.current || !window.google?.maps) return;

    const map = new window.google.maps.Map(mapContainerRef.current, {
      center: location,
      zoom: 13
    });

    const marker = new window.google.maps.Marker({
      position: location,
      map,
      draggable: true
    });

    mapRef.current = map;
    markerRef.current = marker;

    const geocoder = new window.google.maps.Geocoder();

    const updateAddress = (coder, loc) => {
      if (!coder) return;

      coder.geocode({ location: loc }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const result = results[0];
          const formatted = result.formatted_address;

          setAddress(formatted);
          setSelectedAddress({ description: formatted, result });
          setLocation({ lat: loc.lat(), lng: loc.lng() });
        }
      });
    };

    const updateFromPosition = (pos) => {
      marker.setPosition(pos);
      updateAddress(geocoder, pos);
    };

    map.addListener('click', (event) => updateFromPosition(event.latLng));
    marker.addListener('dragend', () =>
      updateFromPosition(marker.getPosition())
    );
  };

  // Debounced autocomplete function to reduce API calls
  const fetchPredictions = useCallback(
    debounce((input) => {
      const service = new window.google.maps.places.AutocompleteService();

      service.getPlacePredictions(
        {
          input,
          componentRestrictions: { country: 'IL' }
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setSuggestions(predictions || []);
          } else {
            notify('Failed to fetch suggestions', { type: 'warning' });
          }
        }
      );
    }, 300),
    [] // make sure this only gets created once
  );

  const handleSearchChange = (event) => {
    const input = event.target.value;

    setSearchInput(input);

    if (!input) {
      setSuggestions([]);
      return;
    }

    fetchPredictions(input);
  };

  const handleSelectAddress = (place) => {
    const placesService = new window.google.maps.places.PlacesService(
      mapRef.current
    );

    placesService.getDetails({ placeId: place.place_id }, (details, status) => {
      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        details.geometry
      ) {
        const geometryLocation = details.geometry.location;
        const formatted = details.formatted_address;

        // Update address state only for valid address
        setAddress(formatted);
        setSelectedAddress({ description: formatted, result: details });
        setLocation({
          lat: geometryLocation.lat(),
          lng: geometryLocation.lng()
        });

        // Move marker and map
        if (markerRef.current && mapRef.current) {
          markerRef.current.setPosition(geometryLocation);
          mapRef.current.setCenter(geometryLocation);
        }
      }
    });

    setSuggestions([]);
  };

  const handleSave = () => {
    // If no address was selected from the dropdown, attempt to geocode manually
    const addressToGeocode = selectedAddress
      ? selectedAddress.description
      : address;

    if (addressToGeocode) {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address: addressToGeocode }, (results, status) => {
        if (status === 'OK') {
          const newLocation = results[0].geometry.location;

          // Extract address components
          const addressComponents = results[0].address_components;
          let city = '';
          let district = '';
          let street = '';

          // Loop through address components to find city, district, and street
          addressComponents.forEach((component) => {
            if (component.types.includes('locality')) {
              city = component.long_name;
            } else if (
              component.types.includes('administrative_area_level_2')
            ) {
              district = component.long_name;
            } else if (component.types.includes('route')) {
              street = component.long_name;
            }
          });

          changeLatitude(newLocation.lat());
          changeLongitude(newLocation.lng());
          changeCity(city);
          changeDistrict(district);
          changeStreet(street);
          changeAddress(addressToGeocode);

          // Pass all the extracted details to onAddressSelect
          onAddressSelect(city, district, changeCity, changeDistrict);
          setSuggestions([]);
          setSearchInput(''); // Clear the search input when saving a valid address
          setOpen(false); // Close the dialog
        } else {
          // Address not valid, show an error
          notify('כתובת לא חוקית! אנא הזן כתובת חוקית', 'error');
        }
      });
    } else {
      // If no address is entered, show an error message
      notify('אין כתובת. אנא בחר כתובת ', 'warning');
    }
  };

  useEffect(() => {
    let timeoutId = null;
    if (open && googleMapsReady) {
      timeoutId = setTimeout(() => initializeMap(), 300);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [open, googleMapsReady]);

  const handleOpen = () => {
    setInitialAddressBeforeDialog(address); // Save original address
    setSearchInput(address); // Populate the search box with the current address
    setOpen(true);
  };

  const handleClose = () => {
    setAddress(initialAddressBeforeDialog); // Restore previous address
    setSuggestions([]); // Clear dropdown suggestions
    setSearchInput(''); // Clear search input when closing the dialog
    setOpen(false); // Close dialog
  };

  const handleOpenAddressDialog = () => setAddressDialogOpen(true);
  const handleCloseAddressDialog = () => setAddressDialogOpen(false);

  const handleSaveAddressDialog = (data) => {
    setAddress(trim(data.address));
    changeAddress(trim(data.address));
    changeStreet(trim(data.street));
    changeCity(data.city);
    changeDistrict(data.district);
    changeLatitude(parseFloat(data.latitude));
    changeLongitude(parseFloat(data.longitude));
  };

  useEffect(() => {
    loadGoogleMaps(() => setGoogleMapsReady(true));
  }, []);

  return (
    <>
      <FormControl>
        <Box>
          <TextField
            label={required ? '* הזן כתובת' : 'הזן כתובת'}
            variant="filled"
            value={address}
            onClick={handleOpen}
            onChange={handleSearchChange} // Updates search input state
            style={{
              width: '255px',
              marginBottom: '10px'
            }}
          />
          <Button
            variant="text"
            className={classes.addressLink}
            onClick={handleOpenAddressDialog}
          >
            הוספת כתובת
          </Button>
        </Box>
      </FormControl>

      <AddressDialog
        open={addressDialogOpen}
        onClose={handleCloseAddressDialog}
        onSave={handleSaveAddressDialog}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        disableAutoFocus
        disableEnforceFocus
      >
        <DialogTitle>בחר כתובת</DialogTitle>
        <DialogContent>
          <TextField
            label="חפש כתובת"
            value={searchInput} // Bind to search input state
            onChange={handleSearchChange}
            fullWidth
            placeholder="הקלד כדי לבצע חיפוש"
            autoFocus
            className={classes.searchBox}
          />
          {suggestions.length > 0 && (
            <Paper className={classes.suggestions}>
              <List>
                {suggestions.map((place) => (
                  <ListItem
                    button
                    key={place.place_id}
                    onClick={() => handleSelectAddress(place)}
                  >
                    {place.description}
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {/* Display the selected address here */}
          {selectedAddress && (
            <div className={classes.selectedAddress}>
              כתובת נבחרת: {selectedAddress.description}
            </div>
          )}

          <div
            id="map"
            ref={mapContainerRef}
            className={classes.mapContainer}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            ביטול
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            שמור
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddressField;
